import { Box } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";

const BOTTOM_GUTTER = 16;
const MIN_SCALE = 0.35;

export function PreviewPane({ children, ...boxProps }) {
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const lastFit = useRef("");
    const [fit, setFit] = useState(null);

    const measure = useCallback(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;
        const column = outer?.parentElement;
        if (!outer || !inner || !column) return;

        let next = null;

        if (getComputedStyle(outer).position === "fixed") {
            const columnStyles = getComputedStyle(column);
            const restingTop =
                column.getBoundingClientRect().top +
                window.scrollY +
                (parseFloat(columnStyles.paddingTop) || 0);

            const naturalWidth = inner.offsetWidth;
            const naturalHeight = inner.offsetHeight;
            const available = window.innerHeight - restingTop - BOTTOM_GUTTER;

            const scale =
                naturalHeight > 0 && available > 0
                    ? Math.max(MIN_SCALE, Math.min(1, available / naturalHeight))
                    : 1;

            next = {
                top: Math.round(restingTop),
                scale: Math.round(scale * 1000) / 1000,
                width: Math.round(naturalWidth * scale),
                height: Math.round(naturalHeight * scale),
            };
        }

        const signature = JSON.stringify(next);
        if (signature !== lastFit.current) {
            lastFit.current = signature;
            setFit(next);
        }
    }, []);

    useEffect(measure);

    useEffect(() => {
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure]);

    return (
        <Box
            {...boxProps}
            ref={outerRef}
            pos={{ base: "relative", md: "fixed" }}
            style={
                fit
                    ? { top: `${fit.top}px`, width: `${fit.width}px`, height: `${fit.height}px` }
                    : undefined
            }
        >
            <Box
                ref={innerRef}
                w={{ base: "auto", md: "max-content" }}
                display={{ base: "block", md: "flow-root" }}
                style={
                    fit && fit.scale < 1
                        ? { transform: `scale(${fit.scale})`, transformOrigin: "top left" }
                        : undefined
                }
            >
                {children}
            </Box>
        </Box>
    );
}
